import React, { useEffect, useState } from "react";

const INITIAL_TIMER_SECONDS = 5 * 60;

export type ParsedTimerDuration =
  | { valid: true; seconds: number }
  | { valid: false };

export function parseTimerDuration(minutesText: string, secondsText: string): ParsedTimerDuration {
  if (!/^\d{1,2}$/.test(minutesText) || !/^\d{1,2}$/.test(secondsText)) return { valid: false };
  const minutes = Number(minutesText);
  const seconds = Number(secondsText);
  if (!Number.isInteger(minutes) || !Number.isInteger(seconds) || minutes > 99 || seconds > 59) return { valid: false };
  const totalSeconds = minutes * 60 + seconds;
  return totalSeconds > 0 ? { valid: true, seconds: totalSeconds } : { valid: false };
}

export function formatTimerDuration(totalSeconds: number) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(safeSeconds / 60)}:${(safeSeconds % 60).toString().padStart(2, "0")}`;
}

export default function WhiteboardTimer() {
  const [timerOpen, setTimerOpen] = useState(false);
  const [minutesText, setMinutesText] = useState("5");
  const [secondsText, setSecondsText] = useState("0");
  const [configuredSeconds, setConfiguredSeconds] = useState(INITIAL_TIMER_SECONDS);
  const [remainingSeconds, setRemainingSeconds] = useState(INITIAL_TIMER_SECONDS);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!running) return;
    const interval = window.setInterval(() => {
      setRemainingSeconds((current) => {
        if (current <= 1) {
          setRunning(false);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [running]);

  const updateDraft = (nextMinutes: string, nextSeconds: string) => {
    setMinutesText(nextMinutes);
    setSecondsText(nextSeconds);
    const duration = parseTimerDuration(nextMinutes, nextSeconds);
    if (!duration.valid) return;
    setConfiguredSeconds(duration.seconds);
    if (!running) setRemainingSeconds(duration.seconds);
    setError(null);
  };

  const readDuration = () => {
    const duration = parseTimerDuration(minutesText, secondsText);
    if (!duration.valid) {
      setError("Enter a whole-number duration from 00:01 to 99:59.");
      return null;
    }
    setConfiguredSeconds(duration.seconds);
    setError(null);
    return duration.seconds;
  };

  const resetTimer = () => {
    const duration = readDuration();
    if (duration == null) return;
    setRunning(false);
    setRemainingSeconds(duration);
  };

  const startOrPauseTimer = () => {
    if (running) {
      setRunning(false);
      return;
    }
    const duration = readDuration();
    if (duration == null) return;
    // A paused timer resumes; a newly chosen or completed timer starts from
    // its chosen duration rather than the original five-minute default.
    if (remainingSeconds <= 0 || duration !== configuredSeconds) setRemainingSeconds(duration);
    setRunning(true);
  };

  const timerLabel = formatTimerDuration(remainingSeconds);
  const invalidDraft = !parseTimerDuration(minutesText, secondsText).valid;

  return (
    <>
      <button type="button" onClick={() => setTimerOpen((open) => !open)} className="inline-flex h-10 shrink-0 items-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100">
        Timer{running || remainingSeconds === 0 ? ` ${timerLabel}` : ""}
      </button>
      {timerOpen && (
        <aside aria-label="Classroom timer" className="fixed bottom-4 right-4 z-50 w-72 rounded-2xl border-2 border-amber-300 bg-white p-4 shadow-2xl">
          <div className="flex justify-between"><b>Classroom timer</b><button type="button" onClick={() => { setRunning(false); setTimerOpen(false); }}>Hide</button></div>
          <div aria-live="polite" className={`my-3 text-center text-5xl font-black tabular-nums ${remainingSeconds === 0 ? "text-rose-600" : "text-slate-900"}`}>{remainingSeconds === 0 ? "Time!" : timerLabel}</div>
          <div className="grid grid-cols-2 gap-2">
            <label>Minutes<input aria-label="Timer minutes" aria-invalid={invalidDraft || undefined} inputMode="numeric" type="number" min="0" max="99" value={minutesText} disabled={running} onChange={(e) => updateDraft(e.target.value, secondsText)} onBlur={() => { if (invalidDraft) setError("Enter a whole-number duration from 00:01 to 99:59."); }} className="w-full border p-2" /></label>
            <label>Seconds<input aria-label="Timer seconds" aria-invalid={invalidDraft || undefined} inputMode="numeric" type="number" min="0" max="59" value={secondsText} disabled={running} onChange={(e) => updateDraft(minutesText, e.target.value)} onBlur={() => { if (invalidDraft) setError("Enter a whole-number duration from 00:01 to 99:59."); }} className="w-full border p-2" /></label>
          </div>
          {error && <p role="alert" className="mt-2 text-sm font-medium text-rose-700">{error}</p>}
          <div className="mt-3 flex gap-2"><button type="button" onClick={startOrPauseTimer} className="flex-1 rounded-xl bg-amber-500 p-2 font-bold text-white">{running ? "Pause" : "Start"}</button><button type="button" onClick={resetTimer} className="rounded-xl border p-2 font-bold">Reset</button></div>
        </aside>
      )}
    </>
  );
}
